import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/utils/api';
import { getImageUrl } from '@/utils/config';
import { EyeIcon } from '@/ui/icons';
import { Lightbox } from '@/ui/Lightbox';
import { ProgressiveImage } from '@/ui/ProgressiveImage';

const BASE_ORIGIN = 'https://mun.esdfl.net';

type GalleryPost = {
  id: number;
  title: string;
  slug: string;
  coverImageUrl?: string;
  coverThumbUrl?: string;
  images?: string[];
  thumbs?: string[];
  description?: string;
  publishedAt?: string;
  viewCount?: number;
};

export function GalleryPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<GalleryPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Combine cover image with gallery images for lightbox (must be before any returns)
  const allImages = useMemo(() => {
    if (!post) return [];
    const imgs: string[] = [];
    if (post.coverImageUrl) imgs.push(getImageUrl(post.coverImageUrl)!);
    if (post.images) imgs.push(...post.images.map((img) => getImageUrl(img)!));
    return imgs;
  }, [post]);

  // Thumbnails for the lightbox selection strip
  const allThumbs = useMemo(() => {
    if (!post) return [];
    const thumbsArr: string[] = [];
    // Use cover thumb or fall back to cover image
    if (post.coverThumbUrl) {
      thumbsArr.push(getImageUrl(post.coverThumbUrl)!);
    } else if (post.coverImageUrl) {
      thumbsArr.push(getImageUrl(post.coverImageUrl)!);
    }
    // Use individual thumbs or fall back to full images
    if (post.images) {
      post.images.forEach((img, idx) => {
        const thumb = post.thumbs?.[idx];
        thumbsArr.push(getImageUrl(thumb || img)!);
      });
    }
    return thumbsArr;
  }, [post]);

  // SEO meta
  const meta = useMemo(() => {
    if (!post) return null;
    const desc = post.description || `${post.title} - ESDFL MUN Galeri`;
    return {
      title: `${post.title} | Galeri | ESDFL MUN`,
      description: desc.slice(0, 160),
      image: post.coverImageUrl ? getImageUrl(post.coverImageUrl) : undefined,
    };
  }, [post]);

  const postUrl = useMemo(() => post ? `${BASE_ORIGIN}/galeri/${post.slug}` : '', [post]);

  // JSON-LD Article
  const jsonLdArticle = useMemo(() => {
    if (!post) return null;
    const data: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: post.title,
      description: post.description || post.title,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      url: postUrl,
      mainEntityOfPage: postUrl,
      author: { '@type': 'Organization', name: 'Edirne Süleyman Demirel Fen Lisesi MUN' },
      publisher: {
        '@type': 'Organization',
        name: 'Edirne Süleyman Demirel Fen Lisesi MUN',
        logo: { '@type': 'ImageObject', url: `${BASE_ORIGIN}/mun_logo.png` }
      }
    };
    if (post.coverImageUrl) data.image = getImageUrl(post.coverImageUrl);
    return data;
  }, [post, postUrl]);

  // JSON-LD Breadcrumb
  const jsonLdBreadcrumb = useMemo(() => {
    if (!post) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${BASE_ORIGIN}/` },
        { '@type': 'ListItem', position: 2, name: 'Galeri', item: `${BASE_ORIGIN}/galeri` },
        { '@type': 'ListItem', position: 3, name: post.title, item: postUrl }
      ]
    };
  }, [post, postUrl]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/gallery/${slug}`);
        if (!ignore) {
          setPost(res.post ?? null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    // Track view
    (async () => {
      try {
        const res = await api.post(`/api/public/gallery_view/${slug}`, {});
        if (!ignore && res.newCount !== undefined) {
          setPost((p) => p ? { ...p, viewCount: res.newCount } : p);
        }
      } catch {}
    })();
    return () => { ignore = true; };
  }, [slug]);

  if (loading) {
    return <div>Yükleniyor…</div>;
  }

  if (!post) {
    return (
      <div className="card p-8 text-center">
        <h1 className="text-xl font-semibold mb-2">Bulunamadı</h1>
        <p className="text-gray-600 mb-4">Bu galeri yazısı bulunamadı.</p>
        <Link to="/galeri" className="btn">Galeriye Dön</Link>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm transition-colors duration-300">
      {meta && (
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <meta property="og:title" content={meta.title} />
          <meta property="og:description" content={meta.description} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={postUrl} />
          {meta.image && <meta property="og:image" content={meta.image} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={meta.title} />
          <meta name="twitter:description" content={meta.description} />
          {meta.image && <meta name="twitter:image" content={meta.image} />}
          <link rel="canonical" href={postUrl} />
          {jsonLdArticle && <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>}
          {jsonLdBreadcrumb && <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>}
        </Helmet>
      )}

      <Link to="/galeri" className="text-brand-600 hover:text-brand-700 mb-4 inline-block">
        ← Galeriye Dön
      </Link>

      <article className="card overflow-hidden">
        {post.coverImageUrl && (
          <ProgressiveImage 
            src={getImageUrl(post.coverThumbUrl || post.coverImageUrl)!} 
            alt={post.title} 
            className="w-full h-64 md:h-96 cursor-pointer" 
            onClick={() => setLightboxIndex(0)}
          />
        )}
        <div className="p-6">
          <h1 className="text-2xl md:text-3xl font-serif font-bold mb-2 text-gray-900 dark:text-white">{post.title}</h1>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-4">
            {post.publishedAt && (
              <span>{new Date(post.publishedAt).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            )}
            <span className="inline-flex items-center gap-1"><EyeIcon /> {post.viewCount ?? 0}</span>
          </div>
          {post.description && (
            <p className="text-gray-700 dark:text-gray-300">{post.description}</p>
          )}
          {post.images && post.images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
              {post.images.map((img, idx) => {
                // Use thumbnail if available, otherwise full image
                const thumbSrc = post.thumbs?.[idx] ? getImageUrl(post.thumbs[idx]) : getImageUrl(img);
                return (
                  <ProgressiveImage 
                    key={idx} 
                    src={thumbSrc!} 
                    alt={`${post.title} - ${idx + 1}`} 
                    className="rounded-lg aspect-[4/3] w-full cursor-pointer hover:scale-[1.02] transition-all" 
                    onClick={() => setLightboxIndex(post.coverImageUrl ? idx + 1 : idx)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </article>

      {/* Lightbox */}
      {lightboxIndex !== null && allImages.length > 0 && (
        <Lightbox
          images={allImages}
          thumbs={allThumbs}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
